/**
 * Home desk analytics (spec §11, PPL-416). Names and properties are the
 * spec's, plus `platform: 'web'` on everything so the app's copies of these
 * events cut the same way. `home_viewed` also writes `home_stage` as a person
 * property (via `$set`) so activation funnels can be cut by stage.
 *
 * Every call goes through the guarded `capture` in services/posthog: no key,
 * no throw, never in the way of a user flow.
 */

import { capture } from '../../services/posthog';
import type { HomeFeed, HomeModule, HomeStage } from '../../types/home';
import { calendarDaysUntil, parseDate } from './format';

export const HomeEvents = {
  VIEWED: 'home_viewed',
  MODULE_SHOWN: 'home_module_shown',
  MODULE_TAPPED: 'home_module_tapped',
  MODULE_DISMISSED: 'home_module_dismissed',
  OPENER_TAPPED: 'home_opener_tapped',
  PROMPT_TAPPED: 'home_prompt_tapped',
} as const;

const PLATFORM = 'web';

export function trackHomeViewed(feed: HomeFeed, opts: { latencyMs: number; fromCache: boolean }): void {
  capture(HomeEvents.VIEWED, {
    stage: feed.stage,
    module_count: feed.modules.length,
    quiet_day: feed.quietDay,
    latency_ms: Math.max(0, Math.round(opts.latencyMs)),
    from_cache: opts.fromCache,
    platform: PLATFORM,
    $set: { home_stage: feed.stage },
  });
}

/** One event per module, at its desk position. The caller dedupes per open. */
export function trackModulesShown(stage: HomeStage, entries: Array<{ module: HomeModule; position: number }>): void {
  entries.forEach(({ module: m, position }) => {
    capture(HomeEvents.MODULE_SHOWN, {
      type: m.type,
      reason_kind: m.reason?.kind ?? null,
      stage,
      position,
      dismiss_key: m.dismissKey,
      platform: PLATFORM,
    });
  });
}

export function trackModuleTapped(stage: HomeStage, module: HomeModule, position: number, target: string): void {
  capture(HomeEvents.MODULE_TAPPED, {
    type: module.type,
    reason_kind: module.reason?.kind ?? null,
    stage,
    position,
    target,
    platform: PLATFORM,
  });
}

/** `until` is homeDismissEurW's answer — an instant, or null for "not again". */
export function trackModuleDismissed(stage: HomeStage, module: HomeModule, until: string | null): void {
  const d = parseDate(until);
  capture(HomeEvents.MODULE_DISMISSED, {
    type: module.type,
    reason_kind: module.reason?.kind ?? null,
    stage,
    window_days: d ? Math.max(0, calendarDaysUntil(d)) : null,
    platform: PLATFORM,
  });
}

export function trackOpenerTapped(stage: HomeStage): void {
  capture(HomeEvents.OPENER_TAPPED, { stage, platform: PLATFORM });
}

export function trackPromptTapped(stage: HomeStage | null, position: number): void {
  capture(HomeEvents.PROMPT_TAPPED, { stage, position, platform: PLATFORM });
}
